/**
 * `profile/edit` — ism, jins, tug'ilgan yil.
 *
 * **Nega bu ekran kerak edi.** `PATCH /me` va `useUpdateMe()` faza 1 dan
 * beri bor edi, lekin ularni chaqiradigan UI YO'Q edi. Natijada 1v1
 * qidiruv `PROFILE_INCOMPLETE` qaytarardi ("avval jins va yoshni
 * ko'rsating"), foydalanuvchi esa ularni hech qayerdan ko'rsata olmasdi —
 * yopiq halqa. Sozlamalardagi "Profilni tahrirlash" qatori "hali emas"
 * deb turardi.
 *
 * Jins va yosh MAJBURIY emas, lekin ularsiz 1v1 ishlamaydi — shuning
 * uchun ekran nima uchun so'ralayotganini ochiq aytadi (D-018: 16+ va
 * foydalanuvchi o'zi ko'rsatadi).
 */

import type { MeUpdateRequest } from '@voice/api-client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useMe, useUpdateMe } from '../lib/api/useMe';
import { Chip, CTAButton, Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import './screens.css';

/** D-018. Server ham tekshiradi (`UNDERAGE`) — bu birinchi qatlam. */
const MIN_AGE = 16;
/** Undan oldin tug'ilgan odam bu ilovada yo'q — kiritish xatosi. */
const MIN_BIRTH_YEAR = 1920;

type Gender = 'male' | 'female';

export function ProfileEdit() {
  const navigate = useNavigate();
  const { data: me, isLoading, isError } = useMe();
  const update = useUpdateMe();
  const toast = useToast();

  const goBack = () => navigate('/profile');
  useBackButton(true, goBack);

  if (isLoading) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.loading}</div>
      </div>
    );
  }

  if (isError || !me) {
    return (
      <div className="screen">
        <div className="empty">{uz.profile.loadFailed}</div>
      </div>
    );
  }

  return <Form me={me} onDone={goBack} toast={toast} update={update} />;
}

/**
 * Forma ALOHIDA komponent.
 *
 * Sabab: boshlang'ich qiymatlar `me` dan keladi va ular `useState` ga
 * faqat BIR MARTA yozilishi kerak. Yuqoridagi komponentda `me` avval
 * `undefined` bo'ladi, ya'ni state bo'sh qiymat bilan qotib qolardi va
 * ma'lumot kelganda formaga tushmasdi.
 */
function Form({
  me,
  onDone,
  toast,
  update,
}: {
  me: NonNullable<ReturnType<typeof useMe>['data']>;
  onDone: () => void;
  toast: ReturnType<typeof useToast>;
  update: ReturnType<typeof useUpdateMe>;
}) {
  const [name, setName] = useState(me.first_name);
  const [gender, setGender] = useState<Gender | null>(
    me.gender === 'male' || me.gender === 'female' ? me.gender : null,
  );
  const [year, setYear] = useState(me.birth_year ? String(me.birth_year) : '');

  const thisYear = new Date().getFullYear();

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.show(uz.profileEdit.nameRequired);
      return;
    }

    // Tug'ilgan yil ixtiyoriy, LEKIN kiritilgan bo'lsa to'g'ri bo'lsin.
    // Bo'sh qoldirilsa maydon umuman yuborilmaydi (`exclude_unset`).
    let birthYear: number | null = null;
    if (year.trim()) {
      const parsed = Number(year);
      if (!Number.isInteger(parsed) || parsed < MIN_BIRTH_YEAR || parsed > thisYear) {
        toast.show(uz.profileEdit.badYear);
        return;
      }
      if (thisYear - parsed < MIN_AGE) {
        toast.show(uz.profileEdit.underage(String(MIN_AGE)));
        return;
      }
      birthYear = parsed;
    }

    const patch: MeUpdateRequest = { first_name: trimmed };
    if (gender) patch.gender = gender;
    if (birthYear !== null) patch.birth_year = birthYear;

    try {
      await update.mutateAsync(patch);
      toast.show(uz.profileEdit.saved);
      onDone();
    } catch (err) {
      // Server yoshni QAYTA tekshiradi (`UNDERAGE`) — klient qo'rig'i
      // chetlab o'tilishi mumkin va yagona haqiqat serverda.
      const code = (err as { code?: string }).code;
      toast.show(
        code === 'UNDERAGE'
          ? uz.profileEdit.underage(String(MIN_AGE))
          : err instanceof Error
            ? err.message
            : uz.common.error,
      );
    }
  }

  return (
    <div className="screen">
      {toast.message && <Toast message={toast.message} />}

      <div className="screen-bar" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="icon-btn" onClick={onDone} aria-label={uz.profile.title}>
          <Icon name="back" size={16} />
        </button>
        <h1 className="screen-bar__title">{uz.profileEdit.title}</h1>
      </div>

      <p className="match__hint">{uz.profileEdit.hint}</p>

      <div className="field-label">{uz.profileEdit.name}</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={uz.profileEdit.namePlaceholder}
        maxLength={64}
      />

      <div className="field-label">{uz.profileEdit.gender}</div>
      <div className="chip-row">
        <Chip active={gender === 'male'} onClick={() => setGender('male')}>
          {uz.profileEdit.male}
        </Chip>
        <Chip active={gender === 'female'} onClick={() => setGender('female')}>
          {uz.profileEdit.female}
        </Chip>
      </div>

      <div className="field-label">{uz.profileEdit.birthYear}</div>
      <input
        value={year}
        onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
        placeholder={uz.profileEdit.birthYearPlaceholder}
        // `type="number"` EMAS: Telegram WebView'da u strelkalar chizadi
        // va `maxLength` ni e'tiborsiz qoldiradi. Raqamli klaviatura
        // `inputMode` bilan ham chiqadi.
        inputMode="numeric"
        className="tnum"
      />

      <div style={{ marginTop: 22 }}>
        <CTAButton onClick={() => void save()} disabled={update.isPending}>
          {update.isPending ? uz.common.loading : uz.profileEdit.save}
        </CTAButton>
      </div>

      <div className="screen-tail screen-tail--flat" />
    </div>
  );
}
